// src/app/App.js
import React from "react";
import RoutesMap from "./routes.jsx";

import Header from "@/app/layout/Header.jsx";
import ErrorBoundary from "@/shared/ErrorBoundary.jsx";
import FeedbackWidget from "@/components/FeedbackWidget.jsx";

import ConfigHealthCheck from "@/features/onboarding/ConfigHealthCheck.jsx";

import { ChakraProvider, extendTheme, ColorModeScript } from "@chakra-ui/react";

import { AuthProvider } from "./providers/AuthProvider.jsx";
import { ConfigProvider } from "./providers/ConfigProvider.jsx";
import { EmbedModeProvider } from "./providers/EmbedModeProvider.jsx";
import { FeatureFlagsProvider } from "./providers/FeatureFlagsProvider.jsx";
import { RealtimeProvider } from "./providers/RealtimeProvider.jsx";
import { BrandProvider } from "./providers/BrandProvider.jsx";


import "@/styles/index.css";

const theme = extendTheme({
  config: {
    initialColorMode: "light",
    useSystemColorMode: false,
  },
  fonts: {
    heading: '"DM Serif Display", Georgia, "Times New Roman", serif',
    body: '"Geist", system-ui, -apple-system, sans-serif',
    mono: '"Geist Mono", ui-monospace, SFMono-Regular, monospace',
  },
  colors: {
    brand: {
      50: "#eff6ff", 100: "#dbeafe", 200: "#bfdbfe", 300: "#93c5fd",
      400: "#60a5fa", 500: "#3b6fd4", 600: "#2563a8", 700: "#1e4f8a",
      800: "#1a3d6d", 900: "#1e2d4a",
    },
  },
  radii: { sm: "8px", md: "12px", lg: "16px" },
  shadows: {
    md: "0 2px 4px rgba(0,0,0,.04), 0 8px 24px rgba(59,111,212,.08)",
    lg: "0 4px 8px rgba(0,0,0,.06), 0 16px 40px rgba(59,111,212,.10)",
  },
  components: {
    Button: {
      defaultProps: { colorScheme: "brand" },
      baseStyle: { fontWeight: 600, borderRadius: "8px" },
    },
    Input: {
      defaultProps: { focusBorderColor: "brand.500" },
    },
    Select: {
      defaultProps: { focusBorderColor: "brand.500" },
    },
    Textarea: {
      defaultProps: { focusBorderColor: "brand.500" },
    },
    Checkbox: {
      defaultProps: { colorScheme: "brand" },
    },
    Switch: {
      defaultProps: { colorScheme: "brand" },
    },
    Tabs: {
      defaultProps: { colorScheme: "brand" },
    },
  },
});

function AppShell() {
  return (
    <>
      <Header />
      <ConfigHealthCheck />
      <ErrorBoundary>
        <RoutesMap />
      </ErrorBoundary>
      <FeedbackWidget />
    </>
  );
}

export default function App() {
  return (
    <ChakraProvider theme={theme}>
      <ColorModeScript initialColorMode={theme.config.initialColorMode} />
      <AuthProvider>
        <EmbedModeProvider>
          <ConfigProvider>
            <BrandProvider>
                <FeatureFlagsProvider>
                  <RealtimeProvider>
                    <AppShell />
                  </RealtimeProvider>
                </FeatureFlagsProvider>
            </BrandProvider>
          </ConfigProvider>
        </EmbedModeProvider>
      </AuthProvider>
    </ChakraProvider>
  );
}
